import {extractGetParamsFromState, GetParams, KAlertType, ParamFilter, setAlert, SortingDirection} from "@kopjra/uikit";
import {store} from "../index";
import {Archetype, Callback, OfficialProductType, UserCredits} from "../types/users";
import FileSaver from "file-saver";
import {PdfSignature} from "../types/pdfSignatures";
import {Timestamp} from "../types/timestamps";
import {BasicSignature} from "../types/signatures";

export function createURLSearchParams(obj: Record<string, any>): URLSearchParams {
    const usp = new URLSearchParams();
    for (const key of Object.keys(obj)) {
        if (obj[key] !== undefined && obj[key] !== null) {
            usp.append(key, obj[key]);
        }
    }
    return usp;
}

export function createDownload(name: string, urlOrBlob: string | Blob) {
    FileSaver.saveAs(urlOrBlob, name);
}

export function tableGetParams(id: string): GetParams {
    return extractGetParamsFromState(store.getState(), id);
}

export function tableHasFilter(id: string): boolean {
    const tableParams = tableGetParams(id);
    return !!tableParams?.filter?.length;
}

export type PartialRecord<T extends string | number | symbol, K> = Partial<Record<T, K>>;

export type QueryParams<T> = {
    top?: number;
    skip?: number;
    sortBy?: string;
    emptyScope?: boolean;
} & PartialRecord<keyof T, string | undefined>;

export type Configuration = { sourceKey?: string; like?: boolean };

export function transformParams<T>(query: GetParams, conf: PartialRecord<keyof T, Configuration>): QueryParams<T> {
    const result: QueryParams<T> = {};
    result.top = query.top;
    result.skip = query.skip;

    if (query.sort && query.direction !== SortingDirection.NONE) {
        result.sortBy = `${(query.direction === SortingDirection.UP) ? "-" : ""}${query.sort}`;
    }

    if (query.filter && query.filter.length > 0) {
        for (const key of Object.keys(conf) as (keyof T)[]) {
            const keyPf = query.filter.find((pf) => (pf.name === (conf[key]!.sourceKey || key)));
            result[key] = (keyPf ? `${conf[key]!.like ? "~" : ""}${keyPf.value}` : undefined) as unknown as QueryParams<T>[keyof T];
        }
    }

    return result;
}

function putTagsInParams<T>(query: GetParams, result: QueryParams<T>): void {
    if (query.filter && query.filter.length > 0) {
        const tagKeyPf = query.filter.find((pf: ParamFilter) => (pf.name === "tagKey"));
        const tagValuePf = query.filter.find((pf: ParamFilter) => (pf.name === "tagValue"));
        if (tagKeyPf?.value) {
            result[`tag:${tagKeyPf.value}` as unknown as keyof T] = tagValuePf?.value as unknown as QueryParams<T>[keyof T];
        }
    }
}

export function transformToBasicSignatureParams(query: GetParams, variation: string): QueryParams<BasicSignature> {
    const result: QueryParams<BasicSignature> = transformParams(query, {
        id: {},
        oURL: {like: true},
        oMethod: {},
        currentStatus: {},
    });
    putTagsInParams(query, result);
    result.variation = variation;
    return result;
}

export function transformToTimestampParams(query: GetParams): QueryParams<Timestamp> {
    return transformParams(query, {
        currentStatus: {},
        trusted: {},
        fileName: {},
    });
}

export function transformToPdfSignatureParams(query: GetParams, isForensic?: boolean): QueryParams<PdfSignature> {
    const result: QueryParams<PdfSignature> = transformParams(query, {
        id: {},
        signerFullname: {like: true},
        signatureType: {},
        currentStatus: {},
        otpSms: {like: true},
    });
    putTagsInParams(query, result);
    if (isForensic !== undefined) {
        result.isForensic = isForensic.toString();
    }
    return result;
}

export function transformToAesSignatureParams(query: GetParams): QueryParams<PdfSignature> {
    return transformToPdfSignatureParams(query, undefined);
}

export function transformToCallbackParams(query: GetParams): QueryParams<Callback> {
    return transformParams(query, {
        resourceType: {},
        endpoint: {like: true},
        onlyFinalStatuses: {},
        events: {},
    });
}

export function transformToArchetypeParams(query: GetParams): QueryParams<Archetype> {
    return transformParams<Archetype>(query, {
        name: {like: true},
    });
}

export function postApplyQuery<T, K extends T = T>(elements: K[], query: QueryParams<T>, modelFields: (keyof T)[]): Paginated<K> {
    elements = elements.filter((e) => {
        let include = true;
        for (const mf of modelFields) {
            if (query[mf]) {
                let value = "";
                let like = false;
                if ((query[mf] as string).startsWith("~")) {
                    value = (query[mf] as string).substring(1);
                    like = true;
                } else {
                    value = (query[mf] as string);
                }

                if (like && !(e[mf] as unknown as string).match(new RegExp(`.*${value}.*`))) {
                    include = false;
                    break;
                } else if (!like && (e[mf] as unknown as string).toString() !== value.toString()) {
                    include = false;
                    break;
                }

            }
        }
        return include;
    });

    if (query.sortBy) {
        const desc = query.sortBy.startsWith("-") ? -1 : 1;
        const prop = (query.sortBy.startsWith("-") ? query.sortBy.substring(1) : query.sortBy) as keyof T;
        elements.sort((a, b) => a[prop] < b[prop] ? -1 * desc : 1 * desc);
    }

    const top = query.top ?? 5;
    const skip = query.skip ?? 0;

    return {
        total: top,
        items: elements.slice(top*skip, Math.min(top, elements.length)),
    };
}

export interface Paginated<S> {
    items: S[];
    total: number;
}

export type Stored<T> = {id: string} & T;

export function copyToClipboard(str: string) {
    const el = document.createElement('textarea');
    el.value = str;
    el.setAttribute('readonly', '');
    el.style.position = 'absolute';
    el.style.left = '-9999px';
    document.body.appendChild(el);
    el.select();
    document.execCommand('copy');
    document.body.removeChild(el);
}

export function showAlert(msg: string, type?: KAlertType) {
    store.dispatch(setAlert(msg, type));
}

export function userIsAllowed(credits?: UserCredits, product?: OfficialProductType): boolean {
    if (!credits) {
        return false;
    } else {
        if (product) {
            return credits[product].isAllowed;
        } else {
            for (const prod of Object.keys(credits)) {
                if (credits[prod].isAllowed) {
                    return true;
                }
            }
            return false;
        }
    }
}

export function removeNullAnUndefined<T extends Record<any, any>>(obj: T): T {
    return Object.fromEntries(Object.entries(obj).filter(([_, v]) => v !== null && v !== undefined)) as T;
}
